import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API, getBackendBaseUrl } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  HeadphonesIcon,
  Clock,
  AlertTriangle,
  CheckCircle,
  Send,
  Filter,
  User,
  Timer,
  Bell,
  BellRing,
  X,
  Paperclip,
  Upload,
  LayoutGrid,
  List
} from 'lucide-react';

// SLA Configuration in hours
const SLA_HOURS = {
  urgent: 4,
  high: 8,
  medium: 24,
  low: 48
};

// Calculate time elapsed and remaining
const calculateTimeInfo = (createdAt, slaDueAt, status) => {
  const now = new Date();
  const created = new Date(createdAt);
  const slaDue = new Date(slaDueAt);
  
  // Time elapsed since creation
  const elapsedMs = now - created;
  const elapsedHours = Math.floor(elapsedMs / (1000 * 60 * 60));
  const elapsedMinutes = Math.floor((elapsedMs % (1000 * 60 * 60)) / (1000 * 60));
  
  // Time remaining until SLA
  const remainingMs = slaDue - now;
  const remainingHours = Math.floor(remainingMs / (1000 * 60 * 60));
  const remainingMinutes = Math.floor(Math.abs(remainingMs % (1000 * 60 * 60)) / (1000 * 60));
  
  // Determine status
  let slaStatus = 'ok'; // green
  let slaColor = 'text-emerald-600 bg-emerald-50';
  
  if (status === 'resolved' || status === 'closed') {
    slaStatus = 'completed';
    slaColor = 'text-zinc-600 bg-zinc-50';
  } else if (remainingMs < 0) {
    slaStatus = 'breached'; // red
    slaColor = 'text-red-600 bg-red-50';
  } else if (remainingMs < 2 * 60 * 60 * 1000) { // less than 2 hours
    slaStatus = 'critical'; // orange
    slaColor = 'text-orange-600 bg-orange-50';
  } else if (remainingMs < 4 * 60 * 60 * 1000) { // less than 4 hours
    slaStatus = 'warning'; // yellow
    slaColor = 'text-amber-600 bg-amber-50';
  }
  
  return {
    elapsed: { hours: elapsedHours, minutes: elapsedMinutes },
    remaining: { hours: Math.abs(remainingHours), minutes: remainingMinutes },
    isBreached: remainingMs < 0,
    slaStatus,
    slaColor
  };
};

// Format time display
const formatTime = (hours, minutes) => {
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

export default function AdminSupport() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [segmentFilter, setSegmentFilter] = useState('all');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [messageAttachments, setMessageAttachments] = useState([]);
  const [draggingTicketId, setDraggingTicketId] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const notifiedTickets = useRef(new Set());
  const messageFileInputRef = useRef(null);

  // Update time every minute for live countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Check for SLA alerts and generate notifications
  useEffect(() => {
    if (tickets.length === 0) return;

    const newNotifications = [];
    
    tickets.forEach(ticket => {
      if (ticket.status === 'resolved' || ticket.status === 'closed') return;
      if (!ticket.created_at || !ticket.sla_due_at) return;
      
      const timeInfo = calculateTimeInfo(ticket.created_at, ticket.sla_due_at, ticket.status);
      const notificationId = `${ticket.ticket_id}-${timeInfo.slaStatus}`;
      
      // Only notify for critical and breached status
      if ((timeInfo.slaStatus === 'critical' || timeInfo.slaStatus === 'breached') && 
          !notifiedTickets.current.has(notificationId)) {
        
        notifiedTickets.current.add(notificationId);
        
        const notification = {
          id: notificationId,
          ticket_id: ticket.ticket_id,
          subject: ticket.subject,
          user_name: ticket.user_name,
          slaStatus: timeInfo.slaStatus,
          remaining: timeInfo.remaining,
          isBreached: timeInfo.isBreached,
          timestamp: new Date(),
          read: false
        };
        
        newNotifications.push(notification);
        
        // Show toast notification
        if (timeInfo.isBreached) {
          toast.error(`SLA Estourado: ${ticket.subject}`, {
            description: `Ticket de ${ticket.user_name} estourou o SLA há ${formatTime(timeInfo.remaining.hours, timeInfo.remaining.minutes)}`,
            duration: 8000,
            action: {
              label: 'Ver',
              onClick: () => loadTicketDetail(ticket)
            }
          });
        } else if (timeInfo.slaStatus === 'critical') {
          toast.warning(`SLA Crítico: ${ticket.subject}`, {
            description: `Restam apenas ${formatTime(timeInfo.remaining.hours, timeInfo.remaining.minutes)} para o SLA`,
            duration: 6000,
            action: {
              label: 'Ver',
              onClick: () => loadTicketDetail(ticket)
            }
          });
        }
      }
    });
    
    if (newNotifications.length > 0) {
      setNotifications(prev => [...newNotifications, ...prev].slice(0, 20));
    }
  }, [tickets, currentTime]);

  // Count unread notifications
  const unreadCount = notifications.filter(n => !n.read).length;

  // Mark notification as read
  const markAsRead = (notificationId) => {
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
  };

  // Mark all as read
  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  // Clear all notifications
  const clearNotifications = () => {
    setNotifications([]);
    notifiedTickets.current.clear();
  };

  const fetchTickets = useCallback(async () => {
    try {
      let url = `${API}/admin/support/tickets`;
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      if (segmentFilter !== 'all') params.append('account_segment', segmentFilter);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, segmentFilter]);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/app', { replace: true });
      return;
    }
    fetchTickets();
  }, [user, navigate, fetchTickets]);

  const loadTicketDetail = async (ticket) => {
    try {
      const response = await fetch(`${API}/support/tickets/${ticket.ticket_id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedTicket(data.ticket);
        setMessages(data.messages);
      }
    } catch (error) {
      toast.error('Erro ao carregar mensagens');
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleMessageAttachments = (files) => {
    const nextFiles = Array.from(files || []).filter(Boolean).slice(0, 5);
    if (nextFiles.length === 0) {
      return;
    }

    const validFiles = [];
    for (const file of nextFiles) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`O arquivo ${file.name} excede 10 MB`);
        continue;
      }
      validFiles.push(file);
    }

    setMessageAttachments((current) => {
      const merged = [...current];
      validFiles.forEach((file) => {
        const exists = merged.some((item) => item.name === file.name && item.size === file.size && item.lastModified === file.lastModified);
        if (!exists) {
          merged.push(file);
        }
      });
      return merged.slice(0, 5);
    });
  };

  const removeMessageAttachment = (fileToRemove) => {
    setMessageAttachments((current) => current.filter((file) => !(file.name === fileToRemove.name && file.size === fileToRemove.size && file.lastModified === fileToRemove.lastModified)));
    if (messageFileInputRef.current) {
      messageFileInputRef.current.value = '';
    }
  };

  const sendMessage = async () => {
    if ((!newMessage.trim() && messageAttachments.length === 0) || !selectedTicket) return;

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('content', newMessage);
      messageAttachments.forEach((file) => {
        formData.append('attachments', file);
      });

      const response = await fetch(`${API}/support/tickets/${selectedTicket.ticket_id}/messages`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const msg = await response.json();
        setMessages([...messages, msg]);
        setNewMessage('');
        setMessageAttachments([]);
        if (messageFileInputRef.current) {
          messageFileInputRef.current.value = '';
        }
        toast.success('Mensagem enviada');
      }
    } catch (error) {
      toast.error('Erro ao enviar mensagem');
    } finally {
      setSending(false);
    }
  };

  const updateTicketStatus = async (ticketId, status) => {
    try {
      const response = await fetch(`${API}/admin/support/tickets/${ticketId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        toast.success('Status atualizado');
        setTickets((current) => current.map((ticket) => (
          ticket.ticket_id === ticketId ? { ...ticket, status } : ticket
        )));
        if (selectedTicket?.ticket_id === ticketId) {
          setSelectedTicket({ ...selectedTicket, status });
        }
        fetchTickets();
      }
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleDragStart = (ticketId) => {
    setDraggingTicketId(ticketId);
  };

  const handleDragEnd = () => {
    setDraggingTicketId(null);
    setDragOverColumn(null);
  };

  const handleDropTicket = async (status) => {
    if (!draggingTicketId) {
      return;
    }

    const ticket = tickets.find((item) => item.ticket_id === draggingTicketId);
    handleDragEnd();

    if (!ticket || ticket.status === status) {
      return;
    }

    await updateTicketStatus(ticket.ticket_id, status);
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-orange-100 text-orange-700',
      in_progress: 'bg-blue-100 text-blue-700',
      resolved: 'bg-emerald-100 text-emerald-700',
      closed: 'bg-zinc-100 text-zinc-700'
    };
    const labels = {
      open: 'Aberto',
      in_progress: 'Em Andamento',
      resolved: 'Resolvido',
      closed: 'Fechado'
    };
    return <Badge className={`${styles[status]} border-0`}>{labels[status]}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      urgent: 'bg-red-100 text-red-700',
      high: 'bg-orange-100 text-orange-700',
      medium: 'bg-amber-100 text-amber-700',
      low: 'bg-emerald-100 text-emerald-700'
    };
    const labels = {
      urgent: 'Urgente',
      high: 'Alta',
      medium: 'Média',
      low: 'Baixa'
    };
    return <Badge className={`${styles[priority]} border-0`}>{labels[priority]}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const groupedTickets = {
    open: tickets.filter((ticket) => ticket.status === 'open'),
    in_progress: tickets.filter((ticket) => ticket.status === 'in_progress'),
    resolved: tickets.filter((ticket) => ticket.status === 'resolved' || ticket.status === 'closed'),
  };

  const getKanbanCardTone = (ticket) => {
    if (!ticket.created_at || !ticket.sla_due_at) {
      return 'border-white bg-white';
    }

    const timeInfo = calculateTimeInfo(ticket.created_at, ticket.sla_due_at, ticket.status);
    const tones = {
      ok: 'border-emerald-200 bg-emerald-50/70',
      warning: 'border-amber-200 bg-amber-50/80',
      critical: 'border-orange-200 bg-orange-50/80',
      breached: 'border-red-200 bg-red-50/80',
      completed: 'border-zinc-200 bg-zinc-50/80',
    };

    return tones[timeInfo.slaStatus] || 'border-white bg-white';
  };

  // SLA Timer Component
  const SlaTimer = ({ ticket }) => {
    if (!ticket.created_at || !ticket.sla_due_at) return null;
    
    const timeInfo = calculateTimeInfo(ticket.created_at, ticket.sla_due_at, ticket.status);
    
    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${timeInfo.slaColor}`}>
        <Timer className="w-4 h-4" />
        <div className="text-xs font-medium">
          {ticket.status === 'resolved' || ticket.status === 'closed' ? (
            <span>Finalizado em {formatTime(timeInfo.elapsed.hours, timeInfo.elapsed.minutes)}</span>
          ) : timeInfo.isBreached ? (
            <span className="flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              SLA estourado há {formatTime(timeInfo.remaining.hours, timeInfo.remaining.minutes)}
            </span>
          ) : (
            <span>
              Restam {formatTime(timeInfo.remaining.hours, timeInfo.remaining.minutes)} para SLA
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <HeadphonesIcon className="w-6 h-6 text-emerald-600" />
              <span className="font-heading text-lg font-semibold">Central de Suporte</span>
            </div>
          </div>
          
          {/* SLA Legend */}
          <div className="hidden md:flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-emerald-500" />
              <span className="text-zinc-600">No prazo</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-amber-500" />
              <span className="text-zinc-600">Atenção</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-orange-500" />
              <span className="text-zinc-600">Crítico</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-zinc-600">Estourado</span>
            </div>
          </div>

          {/* Notifications Button */}
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative"
              data-testid="notifications-btn"
            >
              {unreadCount > 0 ? (
                <BellRing className="w-5 h-5 text-orange-500 animate-pulse" />
              ) : (
                <Bell className="w-5 h-5" />
              )}
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </Button>

            {/* Notifications Panel */}
            {showNotifications && (
              <div className="absolute right-0 top-12 w-80 bg-white rounded-xl shadow-xl border border-zinc-200 z-50 overflow-hidden">
                <div className="p-3 border-b border-zinc-100 flex items-center justify-between bg-zinc-50">
                  <h4 className="font-semibold text-sm text-zinc-900">Alertas de SLA</h4>
                  <div className="flex items-center gap-2">
                    {notifications.length > 0 && (
                      <>
                        <button
                          onClick={markAllAsRead}
                          className="text-xs text-emerald-600 hover:underline"
                        >
                          Marcar lidas
                        </button>
                        <button
                          onClick={clearNotifications}
                          className="text-xs text-zinc-500 hover:text-zinc-700"
                        >
                          Limpar
                        </button>
                      </>
                    )}
                    <button onClick={() => setShowNotifications(false)}>
                      <X className="w-4 h-4 text-zinc-400" />
                    </button>
                  </div>
                </div>
                
                <ScrollArea className="max-h-80">
                  {notifications.length === 0 ? (
                    <div className="p-6 text-center">
                      <Bell className="w-8 h-8 mx-auto text-zinc-300 mb-2" />
                      <p className="text-sm text-zinc-500">Nenhum alerta de SLA</p>
                      <p className="text-xs text-zinc-400 mt-1">Tickets críticos aparecerão aqui</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-zinc-100">
                      {notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`p-3 hover:bg-zinc-50 cursor-pointer transition-colors ${!notification.read ? 'bg-orange-50/50' : ''}`}
                          onClick={() => {
                            markAsRead(notification.id);
                            const ticket = tickets.find(t => t.ticket_id === notification.ticket_id);
                            if (ticket) loadTicketDetail(ticket);
                            setShowNotifications(false);
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              notification.isBreached ? 'bg-red-100' : 'bg-orange-100'
                            }`}>
                              <AlertTriangle className={`w-4 h-4 ${
                                notification.isBreached ? 'text-red-600' : 'text-orange-600'
                              }`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-zinc-900 truncate">
                                {notification.subject}
                              </p>
                              <p className="text-xs text-zinc-500 truncate">
                                {notification.user_name}
                              </p>
                              <p className={`text-xs font-medium mt-1 ${
                                notification.isBreached ? 'text-red-600' : 'text-orange-600'
                              }`}>
                                {notification.isBreached 
                                  ? `Estourado há ${formatTime(notification.remaining.hours, notification.remaining.minutes)}`
                                  : `Restam ${formatTime(notification.remaining.hours, notification.remaining.minutes)}`
                                }
                              </p>
                            </div>
                            {!notification.read && (
                              <div className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0 mt-2" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-4 py-4 md:p-6">
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Tickets List */}
          <div className="lg:col-span-12">
            <Card className="border-zinc-200">
              <CardHeader className="border-b border-zinc-100">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="text-lg">Tickets</CardTitle>
                  <div className="flex w-full flex-col sm:w-auto sm:flex-row gap-2 sm:items-center">
                    <div className="grid grid-cols-2 rounded-lg border border-zinc-200 p-1 bg-white">
                      <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm" className="w-full" onClick={() => setViewMode('list')}>
                        <List className="w-4 h-4 mr-1" />
                        Lista
                      </Button>
                      <Button variant={viewMode === 'kanban' ? 'default' : 'ghost'} size="sm" className="w-full" onClick={() => setViewMode('kanban')}>
                        <LayoutGrid className="w-4 h-4 mr-1" />
                        Kanban
                      </Button>
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-full sm:w-[120px]">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="open">Abertos</SelectItem>
                        <SelectItem value="in_progress">Em Andamento</SelectItem>
                        <SelectItem value="resolved">Resolvidos</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                      <SelectTrigger className="w-full sm:w-[120px]">
                        <SelectValue placeholder="Prioridade" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        <SelectItem value="urgent">Urgente</SelectItem>
                        <SelectItem value="high">Alta</SelectItem>
                        <SelectItem value="medium">Média</SelectItem>
                        <SelectItem value="low">Baixa</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={segmentFilter} onValueChange={setSegmentFilter}>
                      <SelectTrigger className="w-full sm:w-[130px]">
                        <SelectValue placeholder="Segmento" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Geral</SelectItem>
                        <SelectItem value="personal">Pessoal</SelectItem>
                        <SelectItem value="business">Business</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[600px]">
                  {loading ? (
                    <div className="p-8 text-center">
                      <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto" />
                    </div>
                  ) : tickets.length > 0 ? (
                    viewMode === 'list' ? (
                    <div className="divide-y divide-zinc-100">
                      {tickets.map((ticket) => {
                        const timeInfo = ticket.created_at && ticket.sla_due_at 
                          ? calculateTimeInfo(ticket.created_at, ticket.sla_due_at, ticket.status)
                          : null;
                        
                        return (
                          <div
                            key={ticket.ticket_id}
                            className={`p-4 cursor-pointer hover:bg-zinc-50 transition-colors ${
                              selectedTicket?.ticket_id === ticket.ticket_id ? 'bg-emerald-50' : ''
                            }`}
                            onClick={() => loadTicketDetail(ticket)}
                            data-testid={`ticket-${ticket.ticket_id}`}
                          >
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <p className="text-sm font-medium text-zinc-900 line-clamp-1">
                                {ticket.subject}
                              </p>
                              {timeInfo?.slaStatus === 'breached' && (
                                <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                              )}
                            </div>
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              {getStatusBadge(ticket.status)}
                              {getPriorityBadge(ticket.priority)}
                            </div>
                            
                            {/* SLA Timer in list */}
                            {timeInfo && (
                              <div className={`mt-2 text-xs font-medium flex items-center gap-1 ${timeInfo.slaColor.replace('bg-', 'text-').split(' ')[0]}`}>
                                <Clock className="w-3 h-3" />
                                {ticket.status === 'resolved' || ticket.status === 'closed' ? (
                                  <span>Tempo: {formatTime(timeInfo.elapsed.hours, timeInfo.elapsed.minutes)}</span>
                                ) : timeInfo.isBreached ? (
                                  <span>Estourado: +{formatTime(timeInfo.remaining.hours, timeInfo.remaining.minutes)}</span>
                                ) : (
                                  <span>Restam: {formatTime(timeInfo.remaining.hours, timeInfo.remaining.minutes)}</span>
                                )}
                              </div>
                            )}
                            
                            <div className="flex items-center justify-between text-xs text-zinc-500 mt-2">
                              <span>{ticket.user_name}</span>
                              <span>{formatDate(ticket.created_at)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4">
                        {[
                          { key: 'open', label: 'Novos', color: 'border-orange-200 bg-orange-50/40' },
                          { key: 'in_progress', label: 'Em andamento', color: 'border-blue-200 bg-blue-50/40' },
                          { key: 'resolved', label: 'Resolvidos', color: 'border-emerald-200 bg-emerald-50/40' },
                        ].map((column) => (
                          <div
                            key={column.key}
                            className={`rounded-2xl border p-3 transition-colors ${column.color} ${dragOverColumn === column.key ? 'ring-2 ring-emerald-300 border-emerald-300 bg-emerald-50/70' : ''}`}
                            onDragOver={(e) => {
                              e.preventDefault();
                              setDragOverColumn(column.key);
                            }}
                            onDragEnter={(e) => {
                              e.preventDefault();
                              setDragOverColumn(column.key);
                            }}
                            onDragLeave={() => {
                              if (dragOverColumn === column.key) {
                                setDragOverColumn(null);
                              }
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              handleDropTicket(column.key);
                            }}
                          >
                            <div className="flex items-center justify-between mb-3">
                              <p className="text-sm font-semibold text-zinc-900">{column.label}</p>
                              <Badge className="border-0 bg-white text-zinc-700">{groupedTickets[column.key].length}</Badge>
                            </div>
                            <div className="space-y-3">
                              {groupedTickets[column.key].length > 0 ? groupedTickets[column.key].map((ticket) => {
                                const timeInfo = ticket.created_at && ticket.sla_due_at
                                  ? calculateTimeInfo(ticket.created_at, ticket.sla_due_at, ticket.status)
                                  : null;

                                return (
                                  <button
                                    key={ticket.ticket_id}
                                    onClick={() => loadTicketDetail(ticket)}
                                    draggable
                                    onDragStart={() => handleDragStart(ticket.ticket_id)}
                                    onDragEnd={handleDragEnd}
                                    className={`w-full text-left rounded-xl border p-3 shadow-sm transition-colors hover:border-emerald-300 ${getKanbanCardTone(ticket)} ${selectedTicket?.ticket_id === ticket.ticket_id ? 'ring-2 ring-emerald-300' : ''}`}
                                  >
                                    <p className="text-sm font-medium text-zinc-900 line-clamp-2">{ticket.subject}</p>
                                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                                      {getPriorityBadge(ticket.priority)}
                                      {ticket.attachments?.length > 0 && (
                                        <Badge className="bg-violet-100 text-violet-700 border-0 text-[10px]">
                                          {ticket.attachments.length} anexo(s)
                                        </Badge>
                                      )}
                                    </div>
                                    {timeInfo && (
                                      <div className="mt-3 space-y-1">
                                        <div className="flex items-center gap-1 text-[11px] font-medium text-zinc-700">
                                          <Clock className="w-3 h-3" />
                                          <span>Aberto há {formatTime(timeInfo.elapsed.hours, timeInfo.elapsed.minutes)}</span>
                                        </div>
                                        <div className={`flex items-center gap-1 text-[11px] font-medium ${timeInfo.slaColor.replace('bg-', 'text-').split(' ')[0]}`}>
                                          <Timer className="w-3 h-3" />
                                          {ticket.status === 'resolved' || ticket.status === 'closed' ? (
                                            <span>Finalizado em {formatTime(timeInfo.elapsed.hours, timeInfo.elapsed.minutes)}</span>
                                          ) : timeInfo.isBreached ? (
                                            <span>Prazo estourado há {formatTime(timeInfo.remaining.hours, timeInfo.remaining.minutes)}</span>
                                          ) : (
                                            <span>Restam {formatTime(timeInfo.remaining.hours, timeInfo.remaining.minutes)}</span>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    <p className="text-xs text-zinc-500 mt-3">{ticket.user_name}</p>
                                  </button>
                                );
                              }) : (
                                <div className="rounded-xl border border-dashed border-zinc-200 bg-white/70 p-4 text-center text-sm text-zinc-500">
                                  Nenhum ticket nesta coluna
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  ) : (
                    <div className="p-8 text-center text-zinc-500">
                      <HeadphonesIcon className="w-10 h-10 mx-auto mb-3 text-zinc-300" />
                      <p>Nenhum ticket encontrado</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>

        <Sheet
          open={Boolean(selectedTicket)}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedTicket(null);
              setMessages([]);
              setNewMessage('');
              setMessageAttachments([]);
            }
          }}
        >
          <SheetContent side="right" className="w-full max-w-full sm:max-w-3xl overflow-y-auto px-4 sm:px-6">
            {selectedTicket && (
              <div className="space-y-6">
                <SheetHeader>
                  <SheetTitle>{selectedTicket.subject}</SheetTitle>
                  <SheetDescription>
                    #{selectedTicket.ticket_id} • {selectedTicket.user_name} • {selectedTicket.user_email}
                  </SheetDescription>
                </SheetHeader>

                <div className="flex items-center gap-2 flex-wrap">
                  {getStatusBadge(selectedTicket.status)}
                  {getPriorityBadge(selectedTicket.priority)}
                </div>

                <SlaTimer ticket={selectedTicket} />

                <div className="flex flex-col sm:flex-row gap-2">
                  <Select
                    value={selectedTicket.status}
                    onValueChange={(v) => updateTicketStatus(selectedTicket.ticket_id, v)}
                  >
                    <SelectTrigger className="w-full sm:w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Aberto</SelectItem>
                      <SelectItem value="in_progress">Em Andamento</SelectItem>
                      <SelectItem value="resolved">Resolvido</SelectItem>
                      <SelectItem value="closed">Fechado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="bg-zinc-50 p-4 rounded-lg">
                  <p className="text-xs text-zinc-500 mb-1">Descrição original:</p>
                  <p className="text-sm text-zinc-700">{selectedTicket.description}</p>
                  {selectedTicket.attachments?.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <p className="text-xs text-zinc-500">Arquivos enviados pelo cliente:</p>
                      <div className="space-y-2">
                        {selectedTicket.attachments.map((attachment) => (
                          <a
                            key={attachment.attachment_id}
                            href={`${getBackendBaseUrl()}${attachment.download_url}`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-white px-3 py-2 hover:border-emerald-300 hover:bg-emerald-50/40"
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-zinc-900 truncate">{attachment.name}</p>
                              <p className="text-xs text-zinc-500">{formatBytes(attachment.size)}</p>
                            </div>
                            <Paperclip className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-4 text-xs text-zinc-500 flex-wrap">
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    <span>Aberto: {formatDate(selectedTicket.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Timer className="w-3 h-3" />
                    <span>SLA: {formatDate(selectedTicket.sla_due_at)}</span>
                  </div>
                  {selectedTicket.first_response_at && (
                    <div className="flex items-center gap-1">
                      <CheckCircle className="w-3 h-3 text-emerald-500" />
                      <span>1ª resposta: {formatDate(selectedTicket.first_response_at)}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  {messages.map((msg) => (
                    <div
                      key={msg.message_id}
                      className={`p-3 rounded-lg ${
                        msg.is_admin
                          ? 'bg-emerald-50 ml-8'
                          : 'bg-zinc-100 mr-8'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {msg.is_admin ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">Admin</Badge>
                        ) : (
                          <User className="w-3 h-3 text-zinc-400" />
                        )}
                        <span className="text-xs text-zinc-500">{msg.user_name}</span>
                        <span className="text-xs text-zinc-400">{formatDate(msg.created_at)}</span>
                      </div>
                      <p className="text-sm text-zinc-700">{msg.content}</p>
                      {msg.attachments?.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {msg.attachments.map((attachment) => (
                            <a
                              key={attachment.attachment_id}
                              href={`${getBackendBaseUrl()}${attachment.download_url}`}
                              target="_blank"
                              rel="noreferrer"
                              className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-white px-3 py-2 hover:border-emerald-300"
                            >
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-zinc-900 truncate">{attachment.name}</p>
                                <p className="text-xs text-zinc-500">{formatBytes(attachment.size)}</p>
                              </div>
                              <Paperclip className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="space-y-3 border-t border-zinc-100 pt-4">
                <div className="flex gap-2">
                  <Textarea
                      placeholder="Digite sua resposta..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                    className="min-h-[60px] flex-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={(!newMessage.trim() && messageAttachments.length === 0) || sending}
                      className="px-4"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-3">
                    <div>
                      <p className="text-sm font-medium text-zinc-900">Anexar arquivo na resposta</p>
                      <p className="text-xs text-zinc-500">Envie imagem, PDF, áudio ou outros arquivos de apoio.</p>
                    </div>
                    <div>
                      <input
                        ref={messageFileInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(e) => handleMessageAttachments(e.target.files)}
                      />
                      <Button type="button" variant="outline" onClick={() => messageFileInputRef.current?.click()}>
                        <Upload className="w-4 h-4 mr-2" />
                        Escolher arquivos
                      </Button>
                    </div>
                  </div>

                  {messageAttachments.length > 0 && (
                    <div className="space-y-2">
                      {messageAttachments.map((file) => (
                        <div key={`${file.name}-${file.lastModified}`} className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-white px-3 py-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-zinc-900 truncate">{file.name}</p>
                            <p className="text-xs text-zinc-500">{formatBytes(file.size)}</p>
                          </div>
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeMessageAttachment(file)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </main>
    </div>
  );
}
